import { useState, ChangeEvent } from 'react';
import DataFixScriptSection from '../../components/sections/DataFixScriptSection';
import IncidentDetailsSection from '../../components/sections/IncidentDetailsSection';
import DynamicParametersSection, { ParameterField, ParameterRow } from '../../components/sections/DynamicParametersSection';
import PreviewSection from '../../components/sections/PreviewSection';
import Button from '../../components/reusable/Button';
import Modal from '../../components/reusable/Modal';
import '../../styles/ScriptRunUI.css';

interface ScriptConfig {
  value: string;
  label: string;
  description: string;
  impact: string;
  parameters: ParameterField[];
}

const ClaimsCoverageUpdate = () => {
  // Script Configuration
  const scriptOptions: ScriptConfig[] = [
    {
      value: 'update-deleted-claims',
      label: 'Update Deleted Claims Coverages',
      description: 'Script fix to allow updating deleted claims coverages in Infinity',
      impact: 'Claims Coverage Update | Low Impact',
      parameters: [
        {
          name: 'claimcovId',
          label: 'claimcovId',
          type: 'text',
          placeholder: 'Enter claim coverage ID',
          required: true,
        },
        {
          name: 'claimid',
          label: 'claimid',
          type: 'text',
          placeholder: 'Enter claim ID',
          required: true,
        },
      ],
    },
    {
      value: 'update-coverage-status',
      label: 'Update Coverage Status',
      description: 'Update the status of coverage records in the system',
      impact: 'Coverage Status Modification | Medium Impact',
      parameters: [
        {
          name: 'coverageId',
          label: 'Coverage ID',
          type: 'text',
          placeholder: 'Enter coverage ID',
          required: true,
        },
        {
          name: 'newStatus',
          label: 'New Status',
          type: 'dropdown',
          required: true,
          options: [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'pending', label: 'Pending' },
          ],
        },
      ],
    },
  ];

  // State Management
  const [selectedScript, setSelectedScript] = useState<ScriptConfig | null>(null);
  const [parameterRows, setParameterRows] = useState<ParameterRow[]>([
    { id: '1', values: {} }
  ]);
  
  // Incident Details State
  const [incidentId, setIncidentId] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [environmentImpact, setEnvironmentImpact] = useState('Infinity Restore');
  const [publishToProd, setPublishToProd] = useState(false);
  
  // Modal State
  const [showResetModal, setShowResetModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isExecuted, setIsExecuted] = useState(false);
  const [previewData, setPreviewData] = useState<{
    before: ParameterRow[];
    after: ParameterRow[];
  }>({
    before: [],
    after: []
  });

  // Handlers
  const handleScriptChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const script = scriptOptions.find(s => s.value === e.target.value) || null;
    setSelectedScript(script);
    setParameterRows([{ id: '1', values: {} }]); // Reset rows when script changes
    setShowPreview(false);
    setIsExecuted(false);
  };

  const handleAddRow = () => {
    const newId = (parameterRows.length + 1).toString();
    setParameterRows([...parameterRows, { id: newId, values: {} }]);
  };

  const handleDeleteRow = (rowId: string) => {
    setParameterRows(parameterRows.filter(row => row.id !== rowId));
  };

  const handleRowChange = (rowId: string, name: string, value: string | number) => {
    setParameterRows(parameterRows.map(row =>
      row.id === rowId
        ? { ...row, values: { ...row.values, [name]: value } }
        : row
    ));
  };

  const handleIncidentBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const incidentValue = e.target.value;
    if (incidentValue && !requesterEmail) {
      // Simulate auto-populating requester email
      setTimeout(() => {
        setRequesterEmail(`requester_${incidentValue}@tokiomarine.com`);
      }, 500);
    }
  };

  const handlePublishChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    
    if (checked) {
      setShowPublishModal(true);
    } else {
      setPublishToProd(false);
      setEnvironmentImpact('Infinity Restore');
    }
  };

  const confirmPublishToProd = () => {
    setPublishToProd(true);
    setEnvironmentImpact('Production');
    setShowPublishModal(false);
  };

  const cancelPublishToProd = () => {
    setPublishToProd(false);
    setShowPublishModal(false);
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    setSelectedScript(null);
    setParameterRows([{ id: '1', values: {} }]);
    setIncidentId('');
    setRequesterEmail('');
    setEnvironmentImpact('Infinity Restore');
    setPublishToProd(false);
    setShowPreview(false);
    setIsExecuted(false);
    setPreviewData({ before: [], after: [] });
    setShowResetModal(false);
  };

  const handlePreview = () => {
    if (!selectedScript) return;

    // Create preview data from current rows
    setPreviewData({
      before: parameterRows.map(row => ({ ...row })),
      after: isExecuted ? previewData.after : []
    });
    setShowPreview(true);
  };

  const handleExecute = () => {
    if (!selectedScript) return;

    // Validate all rows
    const hasEmptyFields = parameterRows.some(row =>
      selectedScript.parameters.some(param =>
        param.required && !row.values[param.name]
      )
    );

    if (hasEmptyFields || !incidentId || !requesterEmail) {
      setErrorMessage('Please fill in all required fields in all parameter rows.');
      setShowErrorModal(true);
      return;
    }

    setShowExecuteModal(true);
  };

  const confirmExecute = () => {
    setShowExecuteModal(false);

    // Simulate script execution
    setTimeout(() => {
      const success = Math.random() > 0.1; // 90% success rate simulation

      if (success) {
        // Create after data - simulate changes
        const afterData = parameterRows.map(row => ({
          ...row,
          values: { ...row.values, status: 'Updated' }
        }));

        setPreviewData(prev => ({
          ...prev,
          after: afterData
        }));
        setIsExecuted(true);
        setShowSuccessModal(true);
      } else {
        setErrorMessage('Database connection timeout. Please try again.');
        setShowErrorModal(true);
      }
    }, 1500);
  };

  return (
    <div className="script-run-container">
      <div className="script-run-header">
        <h1 className="script-run-title">Data Script Run UI</h1>
        <p className="script-run-subtitle">Claims Coverage Update</p>
      </div>

      <div className="script-run-content">
        {/* Data Fix Script Section */}
        <DataFixScriptSection
          scriptOptions={scriptOptions}
          selectedScript={selectedScript?.value || ''}
          onScriptChange={handleScriptChange}
          description={selectedScript?.description}
          impact={selectedScript?.impact}
        />

        {/* Dynamic Parameters Section - Multi-row mode */}
        {selectedScript && (
          <DynamicParametersSection
            fields={selectedScript.parameters}
            rows={parameterRows}
            onRowChange={handleRowChange}
            onAddRow={handleAddRow}
            onDeleteRow={handleDeleteRow}
            allowMultipleRows={true}
          />
        )}

        {/* Incident Details Section */}
        {selectedScript && (
          <IncidentDetailsSection
            incidentId={incidentId}
            requesterEmail={requesterEmail}
            environmentImpact={environmentImpact}
            publishToProd={publishToProd}
            onIncidentChange={(e) => setIncidentId(e.target.value)}
            onIncidentBlur={handleIncidentBlur}
            onEmailChange={(e) => setRequesterEmail(e.target.value)}
            onEnvironmentChange={(e) => setEnvironmentImpact(e.target.value)}
            onPublishChange={handlePublishChange}
          />
        )}

        {/* Preview Section - Shows after Preview button click */}
        {showPreview && selectedScript && (
          <PreviewSection
            title="Claims Coverage Snapshot Preview"
            fields={selectedScript.parameters}
            beforeData={previewData.before}
            afterData={previewData.after}
            isExecuted={isExecuted}
            showTimestamp={true}
          />
        )}

        {/* Action Buttons */}
        {selectedScript && (
          <div className="action-buttons">
            <Button variant="secondary" onClick={handleReset} size="large">
              Reset
            </Button>
            <Button variant="success" onClick={handleExecute} size="large">
              Execute
            </Button>
            <Button variant="info" onClick={handlePreview} size="large">
              Preview
            </Button>
          </div>
        )}

        
      </div>

      {/* Modals */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Confirm Reset"
        size="small"
      >
        <div className="modal-content-wrapper">
          <p className="modal-message">Are you sure, you want to reset?</p>
          <div className="modal-actions">
            <Button variant="secondary" onClick={() => setShowResetModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={confirmReset}>
              Continue
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showExecuteModal}
        onClose={() => setShowExecuteModal(false)}
        title="Confirm Execution"
        size="small"
      >
        <div className="modal-content-wrapper">
          <p className="modal-message">
            Are you sure, you want to execute the script?
          </p>
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => setShowExecuteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="success" onClick={confirmExecute}>
              Continue
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showPublishModal}
        onClose={cancelPublishToProd}
        title="Publish to Production"
        size="small"
      >
        <div className="modal-content-wrapper">
          <p className="modal-message">
            The script will be published directly to production post approval
          </p>
          <div className="modal-actions">
            <Button variant="primary" onClick={confirmPublishToProd}>
              OK
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        size="small"
      >
        <div className="modal-content-wrapper success-modal">
          <div className="success-icon">✓</div>
          <p className="modal-message">
            Success: Data Script Executed Successfully!
          </p>
          <div className="modal-actions">
            <Button
              variant="success"
              onClick={() => setShowSuccessModal(false)}
              fullWidth
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        size="small"
      >
        <div className="modal-content-wrapper error-modal">
          <div className="error-icon">✕</div>
          <p className="modal-message">
            Error: Data Script Execution Failed
            <br />
            <span className="error-reason">{errorMessage}</span>
          </p>
          <div className="modal-actions">
            <Button
              variant="danger"
              onClick={() => setShowErrorModal(false)}
              fullWidth
            >
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClaimsCoverageUpdate;
